/**
 * The handful of primitives every screen uses.
 *
 * Deliberately small. A kiosk has six screens, three of which are a single
 * decision — a component library would be more surface than the application.
 *
 * What the primitives encode is the one rule the palette cannot enforce on its
 * own: **the brand yellow is a fill and the dark is a fill, and text is only
 * ever the other one of the pair.** `Plaque` exists so an icon sitting on a
 * colour is a component and not a class list somebody gets wrong on the
 * seventh screen.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { Icon } from './icons'
import type { IconName } from './icons'

export function Button({
  children,
  onClick,
  disabled,
  variant = 'primary',
  size = 'regular',
  onDark = false,
  className = '',
  ...rest
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  /**
   * `text` is the one that gets away without a fill or a border — a
   * "cancel", an "add more". It takes its colour from the caller because it
   * is the only variant that appears on both surfaces.
   */
  variant?: 'primary' | 'ghost' | 'text' | 'danger'
  /** `large` is a screen's single call to action. There is at most one. */
  size?: 'regular' | 'large'
  /**
   * This button sits on `--color-dark` rather than on paper.
   *
   * It changes exactly one thing, and it has to: dimming the brand to 40% over
   * #231F20 comes out olive, which reads as a colour somebody chose rather than
   * as a control that is off. Grey at full opacity reads as off on a dark
   * ground the way a dimmed fill reads as off on a light one.
   *
   * It is a prop and not a CSS class because the rule it overrides —
   * `disabled:opacity-40` — is a Tailwind *utility*, and utilities are a later
   * cascade layer than `@layer components`. A `.on-dark:disabled` class loses
   * to it no matter how specific it is, silently: the first attempt at this
   * lived in `styles.css` and did nothing at all.
   */
  onDark?: boolean
  className?: string
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'className'>) {
  const base = {
    primary: 'bg-[var(--color-brand)] text-[var(--color-brand-ink)] shadow-[var(--shadow-glow)] disabled:shadow-none',
    danger: 'bg-[var(--color-bad)] text-white',
    text: 'bg-transparent uppercase',
    ghost: 'border-2 border-[var(--color-line)] bg-transparent'
  }[variant]
  const metrics =
    size === 'large'
      ? 'rounded-[var(--radius-card)] px-14 py-7 text-[length:var(--text-kiosk-lg)]'
      : 'rounded-[var(--radius-pill)] px-10 py-5'
  const off = onDark ? 'disabled:bg-white/10 disabled:text-white/30' : 'disabled:opacity-40'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`tap display-upright inline-flex items-center justify-center gap-4 disabled:cursor-not-allowed ${off} ${metrics} ${base} ${className}`}
      {...rest}
    >
      {children}
    </button>
  )
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-[var(--radius-card)] border border-[var(--color-line)] bg-[var(--color-paper-raised)] ${className}`}
    >
      {children}
    </div>
  )
}

/**
 * An icon on a colour: the dark square with a yellow glyph, or its inverse.
 *
 * It is the repeated unit of this design — the back button, the payment marks
 * and the tick on the success screen are all one of these at three sizes.
 */
export function Plaque({
  icon,
  tone = 'dark',
  size = 'regular',
  className = ''
}: {
  icon: IconName
  /**
   * `onDark` is the third one and it exists because the first two both fail on
   * the action bar: a `dark` plaque on the dark bar is a yellow glyph floating
   * with no shape behind it, and a `paper` one is a white slab punched through
   * the bar. It is the same relationship as `Button`'s `onDark` prop — a fill
   * that reads as a control on a ground that is already a fill.
   */
  tone?: 'dark' | 'paper' | 'onDark'
  size?: 'small' | 'regular' | 'large'
  className?: string
}) {
  const colours = {
    paper: 'bg-[var(--color-paper-sunk)] text-[var(--color-ink)]',
    dark: 'bg-[var(--color-dark)] text-[var(--color-brand)]',
    onDark: 'bg-white/10 text-[var(--color-brand)]'
  }[tone]
  const metrics = {
    small: 'size-20 rounded-[var(--radius-chip)] [&>svg]:size-9',
    regular: 'size-28 rounded-[var(--radius-card)] [&>svg]:size-14',
    large: 'size-40 rounded-[var(--radius-card)] [&>svg]:size-20'
  }[size]
  return (
    <span className={`grid shrink-0 place-items-center ${metrics} ${colours} ${className}`}>
      <Icon name={icon} className="" />
    </span>
  )
}

/**
 * A back button: a `Plaque` that is a target.
 *
 * Four screens open with one and they were four copies of the same three
 * lines. It used to sit in each screen's top-left corner; on a floor-standing
 * 1080x1920 panel that corner is above the customer's eyeline and at the far
 * end of their reach, so every one of them now lives in the screen's
 * `ActionBar` instead — which is why the default tone is the dark one.
 */
export function BackButton({
  onClick,
  label,
  tone = 'onDark'
}: {
  onClick: () => void
  label: string
  tone?: 'dark' | 'paper' | 'onDark'
}) {
  return (
    <button type="button" onClick={onClick} aria-label={label} className="tap shrink-0">
      <Plaque icon="back" tone={tone} size="small" />
    </button>
  )
}

/**
 * The one place a customer's hand goes, on every screen.
 *
 * A floor-standing panel is tilted and 1920px tall: its top third is read and
 * its bottom third is touched, and a control anywhere else asks somebody to
 * reach over their own eyeline. So each step's whole vocabulary — back,
 * cancel, the running total, the one way forward — is this bar, in that order,
 * and a screen's header is left with nothing to press.
 *
 * The dark is not decoration either. It is the surface the total already lives
 * on in the basket, at checkout and under the QR, so the bar reads as one
 * object that follows the customer through the order rather than as four bars
 * that happen to agree. `tone="paper"` is for the two attendant-facing sheets,
 * where a dark band would claim more than a keypad deserves.
 */
export function ActionBar({
  children,
  tone = 'dark',
  className = ''
}: {
  children: ReactNode
  tone?: 'dark' | 'paper'
  className?: string
}) {
  const surface =
    tone === 'dark'
      ? 'bg-[var(--color-dark)] shadow-[var(--shadow-tile)]'
      : 'border-t border-[var(--color-line)] bg-[var(--color-paper-raised)]'
  return (
    <footer
      className={`z-30 flex min-h-[var(--band-foot)] shrink-0 items-center gap-6 px-10 py-7 ${surface} ${className}`}
    >
      {children}
    </footer>
  )
}

/**
 * The passive band across the top of a step.
 *
 * It says whose kiosk this is and which step the customer is on, and it holds
 * no control at all — that is the whole point of it, and the thing to check
 * when one gets added. Anything tappable belongs in `ActionBar`.
 */
export function ScreenHeader({
  title,
  eyebrow,
  children,
  tone = 'paper',
  size = 'regular'
}: {
  title?: string
  eyebrow?: string
  /** The wordmark, a locale switch's ghost, a count — never a target. */
  children?: ReactNode
  tone?: 'brand' | 'paper'
  /**
   * `slim` is for a header that carries no words of its own.
   *
   * The menu's is a wordmark and a pill saying which way the order is going,
   * and at the full band that was 160px of a 1920 panel — 8% of the glass,
   * permanently, for something the customer reads once and never presses.
   * A header with a *title* keeps the full band, because there the height is
   * the title.
   */
  size?: 'regular' | 'slim'
}) {
  return (
    <header
      className={`flex shrink-0 items-center justify-between gap-8 px-10 ${
        size === 'slim' ? 'min-h-[var(--band-head-slim)] py-5' : 'min-h-[var(--band-head)] py-8'
      } ${
        tone === 'brand'
          ? 'bg-[var(--color-brand)] shadow-[var(--shadow-lift)]'
          : 'border-b border-[var(--color-line)] bg-[var(--color-paper-raised)]'
      }`}
    >
      {/*
        Rendered only when there is something to put in it. An empty `<div>`
        here is still a flex child, so a header given only `children` was
        laying out as two columns with `justify-between` pushing the real one
        off its own left edge.
      */}
      {(title !== undefined || eyebrow !== undefined) && (
        <div className="min-w-0">
          {eyebrow !== undefined && (
            <p className={`eyebrow ${tone === 'brand' ? 'text-[var(--color-brand-ink)]/60' : ''}`}>{eyebrow}</p>
          )}
          {title !== undefined && (
            <h1 className="display mt-1 truncate text-[length:var(--text-kiosk-xl)] uppercase">{title}</h1>
          )}
        </div>
      )}
      {children}
    </header>
  )
}

/**
 * A labelled total, on a surface that is already dark.
 *
 * The basket bar and the checkout summary carry the same two lines — a quiet
 * eyebrow over the money in brand — and they are the same object at two sizes,
 * not two designs that happen to agree. The terminal panel is the same money
 * with nothing over it, which is why the label is optional rather than a
 * second component. `AmountPill` is the same idea again for the two places
 * that have to bring their own dark ground with them.
 */
export function Total({
  label,
  value,
  size = 'regular',
  hint,
  className = ''
}: {
  /** Omitted where the surrounding panel has already said what the money is. */
  label?: string
  value: string
  /** `large` is the checkout summary, where the total is the screen's subject. */
  size?: 'regular' | 'large'
  /**
   * Rides in the label, never beside the number.
   *
   * The checkout total is also the control that opens the VAT breakdown, and
   * the disclosure glyph was set next to the money — where it read as part of
   * it. Anything decorating this component belongs on the quiet line.
   */
  hint?: ReactNode
  className?: string
}) {
  return (
    <div className={`min-w-0 ${className}`}>
      {label !== undefined && (
        <p className="eyebrow flex items-center gap-2 text-white/40">
          {label}
          {hint}
        </p>
      )}
      <p
        className={`display truncate text-[var(--color-brand)] ${
          size === 'large' ? 'text-[length:var(--text-kiosk-2xl)]' : 'text-[length:var(--text-kiosk-xl)]'
        }`}
      >
        {value}
      </p>
    </div>
  )
}

/**
 * Money on its own dark pill: the amount due, the amount a QR is for.
 *
 * The number the customer agreed to in the basket is the number they are
 * paying, and it should not be two differently-sized pills that happen to
 * agree.
 */
export function AmountPill({
  children,
  size = 'regular',
  className = ''
}: {
  children: ReactNode
  /**
   * A prop and not a `text-…` in the caller's `className`.
   *
   * (Written without its brackets on purpose: Tailwind scans comments too, and
   * an example of an arbitrary-value class in prose becomes a real rule in the
   * stylesheet.) Both would be arbitrary-value font-size utilities in the same
   * Tailwind layer, so which one wins is decided by the order they happen to
   * be emitted into the stylesheet rather than by the order they are written
   * in the attribute — the payment screen asked for `2xl` and silently got
   * `lg`. This is the same failure `Button`'s `onDark` documents, one layer up.
   */
  size?: 'regular' | 'large'
  className?: string
}) {
  return (
    <p
      className={`display rounded-[var(--radius-pill)] bg-[var(--color-dark)] px-14 py-6 text-[var(--color-brand)] ${
        size === 'large' ? 'text-[length:var(--text-kiosk-2xl)]' : 'text-[length:var(--text-kiosk-lg)]'
      } ${className}`}
    >
      {children}
    </p>
  )
}

/*
 * How many sheets are open.
 *
 * A count and not a boolean because sheets stack — the payment screen opens the
 * terminal sheet over the company-receipt sheet — and the second one closing
 * must not un-inert the page while the first is still up.
 */
let openSheets = 0

/** The application root, which is everything a sheet is modal *over*. */
const appRoot = (): HTMLElement | null => document.getElementById('app')

/**
 * A modal.
 *
 * `inert` on the rest of the page rather than a focus trap: a kiosk has no
 * keyboard for a customer to tab with, and `inert` also stops a stray touch on
 * the menu behind the sheet.
 *
 * That was the design and for a long time it was only the comment. Nothing in
 * the tree ever set `inert` on anything, so the page behind an open sheet kept
 * its focus order and stayed in the accessibility tree: a screen reader walked
 * straight from the product sheet into the menu grid underneath it, and the
 * barcode wedge could still drive a control nobody could see. The scrim caught
 * stray *touches*, which is why it never showed up as a visible bug.
 *
 * Making it true needs the sheet to be somewhere else in the document, because
 * a sheet rendered inside `#app` cannot be exempt from `#app`'s own `inert`.
 * So it portals to `document.body` and marks the application root inert while
 * it is up. `inert` is a single attribute that removes a subtree from focus,
 * from hit-testing and from assistive technology at once — which is exactly the
 * three things a modal wants and what a hand-written focus trap gets wrong.
 *
 * It rises from the bottom edge and keeps its top corners round, because on a
 * portrait panel the customer's hand is at the bottom of the screen and a sheet
 * that arrives under it is one they can reach without reaching.
 */
export function Sheet({
  open,
  onClose,
  children,
  labelledBy,
  width = 'regular',
  height = 'content'
}: {
  open: boolean
  onClose?: () => void
  children: ReactNode
  labelledBy?: string
  width?: 'regular' | 'wide'
  /**
   * `tall` is a ceiling, not a floor: `max-h-[88vh]`. The product sheet is
   * the only one that wants it. Three shot rows must not leave a blank page
   * under them, and a latte with every extra must still scroll behind a
   * pinned "add" rather than grow off the glass.
   */
  height?: 'content' | 'tall'
}) {
  useEffect(() => {
    if (!open) return
    openSheets += 1
    // `inert` is reflected as a property in every engine this ships to; the
    // attribute form is set too so it is visible to anything reading the DOM.
    if (openSheets === 1) appRoot()?.setAttribute('inert', '')
    return () => {
      openSheets -= 1
      if (openSheets === 0) appRoot()?.removeAttribute('inert')
    }
  }, [open])

  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <button
        type="button"
        aria-label="close"
        onClick={onClose}
        /*
          A fill and not a `backdrop-blur`. This scrim covers the whole
          1080x1920 panel and the product sheet opens on every tile tap, so a
          blur is a full-screen filter pass on the hottest path in the app —
          for an effect a customer sees for 400ms behind an opaque sheet. At
          80% the dark is already a stronger scrim than the black/45 it
          replaced.
        */
        className="animate-fade-in absolute inset-0 cursor-default bg-[var(--color-dark)]/80"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`animate-slide-up relative z-10 flex w-full flex-col overflow-hidden rounded-t-[var(--radius-sheet)] bg-[var(--color-paper-raised)] shadow-[var(--shadow-tile)] ${
          height === 'tall' ? 'max-h-[88vh]' : 'max-h-[94vh]'
        } ${width === 'wide' ? 'max-w-6xl' : 'max-w-4xl'}`}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

/**
 * A message the customer must be able to act on, not a toast that vanishes.
 *
 * It announces itself. Every use of this component is a thing that has just
 * become true and that the customer has to do something about — a payment that
 * failed, a line dropped from the basket, a menu too old to sell from — and a
 * screen reader was told about none of them, because the element simply
 * appeared. `bad` asserts, the rest are polite: a refused card interrupts, a
 * stale-menu warning waits for a gap.
 */
export function Notice({
  tone = 'warn',
  children,
  action
}: {
  /**
   * `plain` is the fourth because a statement is not a warning. A test sale
   * files no e-Barimt, and drawing that in amber tells a customer something is
   * wrong when the answer is simply "nothing is coming".
   */
  tone?: 'warn' | 'bad' | 'good' | 'plain'
  children: ReactNode
  action?: ReactNode
}) {
  const colour =
    tone === 'bad'
      ? 'border-[var(--color-bad)] text-[var(--color-bad)]'
      : tone === 'good'
        ? 'border-[var(--color-good)] text-[var(--color-good)]'
        : tone === 'plain'
          ? 'border-[var(--color-ink-ghost)] text-[var(--color-ink-faint)]'
          : 'border-[var(--color-warn)] text-[color:oklch(0.42_0.1_75)]'
  return (
    <div
      role={tone === 'bad' ? 'alert' : 'status'}
      aria-live={tone === 'bad' ? 'assertive' : 'polite'}
      className={`flex items-center gap-4 rounded-[var(--radius-card)] border-2 bg-[var(--color-paper-raised)] px-6 py-4 ${colour}`}
    >
      <div className="flex-1 text-[length:var(--text-kiosk-sm)] font-semibold">{children}</div>
      {action}
    </div>
  )
}

export function Spinner({ label, tone = 'ink' }: { label?: string; tone?: 'ink' | 'brand' }) {
  return (
    <div className="flex flex-col items-center gap-4" role="status" aria-live="polite">
      <div
        className={`size-16 animate-spin rounded-full border-8 border-t-transparent ${
          tone === 'brand'
            ? 'border-[var(--color-brand)]'
            : 'border-[var(--color-brand-strong)]/25 border-t-[var(--color-brand-strong)]'
        }`}
      />
      {label !== undefined && <p className="text-[var(--color-ink-soft)]">{label}</p>}
    </div>
  )
}

/**
 * A quantity stepper.
 *
 * At one, minus removes the line — which is what a customer means by pressing
 * it, and what the domain's `setQuantity` already does at zero.
 *
 * It lives here rather than beside the basket because three screens use it and
 * only one of them is the basket; it used to be exported from `cart-rail.tsx`,
 * which meant the product modal imported a screen to get a button.
 */
export function Stepper({ quantity, onChange }: { quantity: number; onChange: (next: number) => void }) {
  return (
    <div className="flex shrink-0 items-center gap-2 rounded-[var(--radius-pill)] border-2 border-[var(--color-line)] bg-[var(--color-paper-sunk)] p-1.5">
      <button
        type="button"
        aria-label="−"
        onClick={() => onChange(quantity - 1)}
        className="tap grid size-18 min-h-0 place-items-center rounded-full text-[var(--color-ink-faint)] active:bg-[var(--color-line)] active:text-[var(--color-ink)]"
      >
        <Icon name="minus" className="size-9" />
      </button>
      <span className="display-upright w-14 text-center text-[length:var(--text-kiosk-lg)] tabular-nums">
        {quantity}
      </span>
      <button
        type="button"
        aria-label="+"
        onClick={() => onChange(quantity + 1)}
        className="tap grid size-18 min-h-0 place-items-center rounded-full text-[var(--color-ink-faint)] active:bg-[var(--color-line)] active:text-[var(--color-ink)]"
      >
        <Icon name="plus" className="size-9" />
      </button>
    </div>
  )
}

function SteamMark({ className = 'size-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true" focusable="false">
      <path
        d="M4.5 13c0-2.6 2.4-2.6 2.4-5.2M8.2 13.2c0-3 2.6-2.8 2.6-5.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function FlakeMark({ className = 'size-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true" focusable="false">
      <path
        d="M8 2v12M2 8h12M3.8 3.8l8.4 8.4M12.2 3.8L3.8 12.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * Hot or iced, as a colour and a mark — not a word covering the cup.
 *
 * On a tile the photographs are tall now, so a tracked label across the
 * corner was stealing the drink. The sheet has the room: `size="label"`
 * prints the word next to the same mark.
 */
export function TemperatureTag({
  temperature,
  label,
  iceLabel,
  size = 'mark',
  className = ''
}: {
  temperature: 'hot' | 'ice' | 'both'
  label: string
  iceLabel?: string
  size?: 'mark' | 'label'
  className?: string
}) {
  const both = temperature === 'both' && iceLabel !== undefined
  const spoken = both ? `${label} · ${iceLabel}` : label
  const fill = temperature === 'ice' ? 'bg-[var(--color-ice)]' : 'bg-[var(--color-hot)]'

  if (size === 'label' && !both) {
    return (
      <span
        className={`inline-flex items-center gap-2.5 rounded-full py-1.5 pr-4 pl-2.5 text-white ring-2 ring-white/90 ${fill} ${className}`}
      >
        {temperature === 'ice' ? <FlakeMark className="size-5" /> : <SteamMark className="size-5" />}
        <span className="display-upright text-[length:var(--text-kiosk-sm)] tracking-widest uppercase">{label}</span>
      </span>
    )
  }

  if (both) {
    return (
      <span role="img" aria-label={spoken} className={`inline-block size-9 ${className}`}>
        {/*
          The split is a positioned child, not this node. Callers pin the
          tag with `absolute`; a `relative` on the same element lost to it
          or beat it, and the disc sat on the name instead of the cup.
        */}
        <span className="relative block size-full overflow-hidden rounded-full text-white ring-2 ring-white/90">
          <span
            className="absolute inset-0 bg-[var(--color-hot)]"
            style={{ clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
          />
          <span
            className="absolute inset-0 bg-[var(--color-ice)]"
            style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
          />
          <span
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'linear-gradient(to bottom right, transparent calc(50% - 1px), rgb(255 255 255 / 0.85), transparent calc(50% + 1px))'
            }}
          />
          <SteamMark className="absolute top-[18%] left-[16%] size-3" />
          <FlakeMark className="absolute right-[14%] bottom-[14%] size-3" />
        </span>
      </span>
    )
  }

  return (
    <span
      role="img"
      aria-label={spoken}
      className={`inline-grid size-9 place-items-center rounded-full text-white ring-2 ring-white/90 ${
        temperature === 'ice' ? 'bg-[var(--color-ice)]' : 'bg-[var(--color-hot)]'
      } ${className}`}
    >
      {temperature === 'ice' ? <FlakeMark className="size-4" /> : <SteamMark className="size-4" />}
    </span>
  )
}

/**
 * A product image, or a legible stand-in.
 *
 * The old app shipped every image inside the binary so its delta patcher would
 * carry them, and served them through a custom protocol whose path resolution
 * had four branches. These are URLs; a missing one — or one that does not
 * load — is a cup on a tile tinted from the product's name, which is better on
 * a menu than a broken-image glyph and better than a bare initial — a row of
 * tiles all reading "Л" tells the customer nothing.
 *
 * The stand-in does not print the name. Every place it appears — a menu tile, a
 * basket line, a product sheet — sets the name in type an inch away, and the
 * tile was saying it twice.
 */
export function ProductImage({
  src,
  name,
  className = '',
  fit = 'cover'
}: {
  src?: string
  name: string
  className?: string
  /**
   * `contain` is for the product sheet's hero and nothing else.
   *
   * The photographs are a cup shot upright against a pale ground, so a 3:4
   * tile is the same shape as the cup and `cover` keeps it in frame. A
   * full-width hero band is a different shape entirely, and `cover` there cut
   * the cup off at both ends and showed the customer a horizontal slice of
   * its middle.
   */
  fit?: 'cover' | 'contain'
}) {
  /**
   * The URL that failed, not a boolean.
   *
   * A row's `imageUrl` can outlive the object it names — it is typed by hand
   * in the console when the bucket is unconfigured, and an upload the console
   * later replaces leaves the old key referenced until the pull catches up. The
   * browser answers that with a broken-image glyph, which is the one thing this
   * component exists to keep off the menu.
   *
   * It holds the URL because a tile is reused: the menu grid re-renders the
   * same component as the customer scrolls and as a section changes, so a
   * `true` here would follow one dead URL onto the next product's photograph
   * and hide a picture that loads perfectly well.
   */
  const [failedSrc, setFailedSrc] = useState<string>()

  if (src === undefined || src.length === 0 || src === failedSrc) {
    const hue = [...name].reduce((n, c) => n + c.codePointAt(0)!, 0) % 360
    return (
      <div
        className={`grid place-items-center ${className}`}
        style={{ background: `oklch(0.95 0.03 ${hue})`, color: `oklch(0.62 0.09 ${hue})` }}
        aria-hidden="true"
      >
        <Icon name="cup" className="size-12 opacity-50" />
      </div>
    )
  }
  return (
    <img
      src={src}
      alt=""
      /*
        A cup is not something to pick up. Chromium starts a native image drag
        from a press held on a photograph, and the tile then follows the finger
        as a ghost while the tap that was meant to open the drink never lands.
        `styles.css` says the same thing for every image; the attribute is here
        as well because it is the one a reader of this component will look for.
      */
      draggable={false}
      loading="lazy"
      /*
        Decoded off the main thread. `loading="lazy"` decides *when* a
        photograph is fetched and says nothing about what happens once it
        arrives: the default is a synchronous decode on the thread that is
        running the scroll, so a row of tiles entering the viewport stalls the
        one animation the customer is looking at. The tile has already reserved
        its 3:4 box, so nothing moves when the picture lands late.
      */
      decoding="async"
      /*
        A 404, a DNS failure, a file the bucket no longer holds: all of them
        arrive here, and all of them mean the same thing to the customer, so
        the tile falls back to the stand-in it would have drawn had the row
        carried no URL at all. Nothing moves when it does — the caller has
        already reserved the box.
      */
      onError={() => setFailedSrc(src)}
      className={`${fit === 'contain' ? 'object-contain' : 'object-cover'} ${className}`}
    />
  )
}
